import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  Baby,
  Brain,
  CheckCircle2,
  ChevronRight,
  HeartHandshake,
  LockKeyhole,
  MessageCircle,
  ShieldCheck,
  Sparkles,
  Star,
  Zap,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Card } from "./components/ui/card";
import { getDictionary, localeDir, locales, type Locale } from "./i18n";

type Section = "overview" | "hatchery" | "growth" | "skills" | "community" | "pricing" | "support" | "account";

const nav: Section[] = ["overview", "hatchery", "growth", "skills", "community", "pricing", "support", "account"];

const defaultDraft = {
  name: "Mira",
  purpose: "Help me stay steady, prepared, and connected across my day.",
  boundary: "Ask before any cross-site action or memory sync.",
};

function useHashSection() {
  const [section, setSection] = useState<Section>(() => {
    const hash = window.location.hash.replace("#", "") as Section;
    return nav.includes(hash) ? hash : "overview";
  });

  useEffect(() => {
    const onHash = () => {
      const hash = window.location.hash.replace("#", "") as Section;
      setSection(nav.includes(hash) ? hash : "overview");
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  return section;
}

function useLocalState<T>(key: string, initial: T) {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);
  return [value, setValue] as const;
}

function setHash(section: Section) {
  window.location.hash = section;
}

let deferredInstallPrompt: any = null;
function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)")?.matches || (window.navigator as any).standalone === true;
}
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  deferredInstallPrompt = e;
});
async function realInstall() {
  if (isStandalone()) {
    alert("Sovasta Companion is already installed on this device.");
    return;
  }
  if (deferredInstallPrompt) {
    deferredInstallPrompt.prompt();
    await deferredInstallPrompt.userChoice.catch(() => {});
    deferredInstallPrompt = null;
    return;
  }
  alert(/iPad|iPhone|iPod/.test(navigator.userAgent)
    ? "In Safari, tap Share, then Add to Home Screen."
    : "Open your browser menu and choose Install app (or Add to Home screen).");
}

const GUIDE_LS = "sovecompanion.guideUsed.v1";
function loadGuideUsed(): Record<string, boolean> {
  try { return JSON.parse(localStorage.getItem(GUIDE_LS) || "{}"); } catch { return {}; }
}
function markGuideUsedGlobal(key: string) {
  try {
    const used = loadGuideUsed();
    if (!used[key]) { used[key] = true; localStorage.setItem(GUIDE_LS, JSON.stringify(used)); }
  } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent("sovecompanion:guide-updated"));
}

export default function App() {
  const section = useHashSection();
  const [locale, setLocale] = useLocalState<Locale>("sovecompanion.locale", "en");
  const [draft, setDraft] = useLocalState("sovecompanion.draft", defaultDraft);
  const [onboardingOpen, setOnboardingOpen] = useLocalState("sovecompanion.onboarding", true);
  const [user, setUser] = useState<any>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const t = useMemo(() => getDictionary(locale), [locale]);
  const dir = localeDir(locale);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
    document.title = `${section === "overview" ? "Hatch your companion" : t[section]} | Sovasta Companion`;
  }, [dir, locale, section, t]);

  useEffect(() => {
    fetch("/api/sove/session", { credentials: "same-origin" })
      .then((r) => r.json())
      .then((j) => setUser(j.authenticated ? j.identity : null))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (section === "account" && user) markGuideUsedGlobal("account");
    if (section === "pricing") markGuideUsedGlobal("pricing");
    if (section === "growth") markGuideUsedGlobal("growth");
    if (section === "skills") markGuideUsedGlobal("skills");
  }, [section, user]);

  return (
    <main className="min-h-screen overflow-x-hidden bg-sovereign text-text" dir={dir}>
      <Aurora />
      <TopNav t={t} locale={locale} setLocale={setLocale} section={section} user={user} onSignOut={() => {
        fetch("/api/sove/logout", { method: "POST", credentials: "same-origin" }).finally(() => setUser(null));
      }} onOpenAuth={() => setAuthModalOpen(true)} />
      {authModalOpen && <SignInModal t={t} onClose={() => setAuthModalOpen(false)} />}
      {onboardingOpen && <Onboarding t={t} onClose={() => setOnboardingOpen(false)} />}
      <GuideWidget t={t} setHash={setHash} />
      <div className="mx-auto max-w-6xl px-5 pb-12 pt-20 md:pt-24">
        {section === "overview" && (
          <>
            <GuideHeroCard t={t} setHash={setHash} />
            <Overview t={t} />
            <CompetitiveUpgrade t={t} />
          </>
        )}
        {section === "hatchery" && <Hatchery t={t} draft={draft} setDraft={setDraft} />}
        {section === "growth" && <Growth t={t} />}
        {section === "skills" && <Skills t={t} />}
        {section === "community" && <Community t={t} user={user} onOpenAuth={() => setAuthModalOpen(true)} />}
        {section === "pricing" && <Pricing t={t} />}
        {section === "support" && <Support t={t} />}
        {section === "account" && <Account t={t} user={user} onOpenAuth={() => setAuthModalOpen(true)} />}
      </div>
      <Footer t={t} section={section} />
    </main>
  );
}

function Aurora() {
  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-sovereign" />
    </div>
  );
}

function TopNav({ t, locale, setLocale, section, user, onSignOut, onOpenAuth }: any) {
  return (
    <header className="fixed inset-x-0 top-0 z-40 border-b border-black/10 bg-sovereign/90 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4">
        <button onClick={() => setHash("overview")} className="flex items-center gap-3 font-black">
          <span className="grid h-9 w-9 place-items-center rounded-2xl border border-pink/50 bg-pink/10 text-pink">
            <Sparkles size={18} />
          </span>
          {t.product}
        </button>
        <nav className="hidden items-center gap-1 lg:flex">
          {nav.map((item) => (
            <button
              key={item}
              onClick={() => setHash(item)}
              className={`rounded-full px-4 py-2 text-sm font-bold transition ${
                section === item ? "bg-pink/10 text-text ring-1 ring-pink/50" : "text-muted hover:text-text"
              }`}
            >
              {t[item]}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <select
            aria-label="Language"
            value={locale}
            onChange={(event) => setLocale(event.target.value)}
            className="w-14 rounded-full border border-black/10 bg-white px-2 py-2 text-center text-sm font-bold text-text outline-none focus:ring-2 focus:ring-pink"
          >
            {locales.map((item) => (
              <option className="bg-sovereign" key={item.code} value={item.code} title={item.label}>
                {item.code.toUpperCase()}
              </option>
            ))}
          </select>
          {user ? (
            <Button onClick={onSignOut}>{t.signout}</Button>
          ) : (
            <Button onClick={onOpenAuth}>{t.signin}</Button>
          )}
        </div>
      </div>
    </header>
  );
}

function SignInModal({ t, onClose }: any) {
  const standalone = isStandalone();
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-sovereign/80 p-5 backdrop-blur-md" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Card className="max-w-md border-pink/30 bg-white/95 text-text">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-2xl font-black">{t.signinModalTitle}</h2>
          <Button variant="secondary" onClick={onClose}>{t.close}</Button>
        </div>
        <p className="text-muted">{t.signinModalBody}</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Button onClick={() => (window.location.href = "/api/sove/login")}>{t.signinModalContinue}</Button>
          {!standalone && (
            <Button variant="secondary" onClick={() => { onClose(); realInstall(); }}>{t.install}</Button>
          )}
        </div>
      </Card>
    </div>
  );
}

function GuideHeroCard({ t, setHash }: any) {
  const [, force] = useState(0);
  useEffect(() => {
    const handler = () => force((x) => x + 1);
    window.addEventListener("sovecompanion:guide-updated", handler);
    return () => window.removeEventListener("sovecompanion:guide-updated", handler);
  }, []);
  const used = loadGuideUsed();
  const done = GUIDE_SLIDES.filter((s) => used[s.key]).length;
  const title = done === 0 ? t.guideHeroTitle : done >= GUIDE_SLIDES.length ? t.guideHeroDone : t.guideHeroTitle;
  const eyebrow = done === 0 || done >= GUIDE_SLIDES.length ? t.guideHeroEyebrow : `${t.guideHeroEyebrow} · ${done}/${GUIDE_SLIDES.length}`;
  return (
    <Card className="mb-6 flex flex-wrap items-center justify-between gap-4 border-pink/30 bg-pink/5">
      <div>
        <p className="text-xs font-black uppercase tracking-[0.28em] text-pink">{eyebrow}</p>
        <p className="mt-2 font-black">{title}</p>
      </div>
      <Button onClick={() => window.dispatchEvent(new CustomEvent("sovecompanion:open-guide"))}>{t.guideHeroCta}</Button>
    </Card>
  );
}

const GUIDE_SLIDES = [
  { key: "hatchery", view: "hatchery", icon: "🥚", t: "Hatch a companion", d: "Name it, set its purpose and boundaries — no sign-in needed to try." },
  { key: "growth", view: "growth", icon: "🌱", t: "Growth loop", d: "Track level, streak, and five core stats as your companion earns trust." },
  { key: "skills", view: "skills", icon: "🔓", t: "Permission tree", d: "Unlock care, work, social, and trust skills — each one is explicit, never silent." },
  { key: "community", view: "community", icon: "🗣️", t: "Community", d: "Tell us what your companion still needs, and see what other members are asking for." },
  { key: "account", view: "account", icon: "👤", t: "Account (sign in)", d: "Sign in with Sovasta ID to save your companion and sync memory." },
  { key: "pricing", view: "pricing", icon: "💳", t: "Pricing", d: "Free hatches one local companion. Paid tiers unlock sync, family rules, and evidence reports." },
];

function GuideWidget({ t, setHash }: any) {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [list, setList] = useState(GUIDE_SLIDES);
  useEffect(() => {
    const openHandler = () => {
      const used = loadGuideUsed();
      const pending = GUIDE_SLIDES.filter((s) => !used[s.key]);
      setList(pending.length ? pending : GUIDE_SLIDES);
      setIdx(0);
      setOpen(true);
    };
    window.addEventListener("sovecompanion:open-guide", openHandler);
    return () => window.removeEventListener("sovecompanion:open-guide", openHandler);
  }, []);
  useEffect(() => {
    if (!open) return;
    const timer = setInterval(() => setIdx((i) => (i + 1) % list.length), 4500);
    return () => clearInterval(timer);
  }, [open, list]);
  const slide = list[idx % list.length];
  return (
    <>
      <button
        aria-label="Feature guide"
        onClick={() => window.dispatchEvent(new CustomEvent("sovecompanion:open-guide"))}
        className="fixed right-4 top-3.5 z-[60] grid h-9 w-9 place-items-center rounded-full bg-pink font-black text-white shadow-lg"
      >
        ?
      </button>
      {open && (
        <div className="fixed right-4 top-16 z-[65] w-72 rounded-2xl border border-black/10 bg-white p-4 text-text shadow-xl">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-black uppercase tracking-[0.12em] text-pink">FEATURE GUIDE</span>
            <button onClick={() => setOpen(false)} className="text-lg text-muted">×</button>
          </div>
          <div className="text-2xl">{slide.icon}</div>
          <h4 className="mt-1 font-black">{slide.t}</h4>
          <p className="mt-1 text-sm leading-5 text-muted">{slide.d}</p>
          <div className="mt-3 flex items-center justify-between gap-2">
            <Button onClick={() => { setHash(slide.view); setOpen(false); }}>Take me there →</Button>
            <div className="flex gap-1">
              {list.map((_, i) => (
                <span key={i} onClick={() => setIdx(i)} className={`h-1.5 w-1.5 cursor-pointer rounded-full ${i === idx ? "bg-pink" : "bg-black/15"}`} />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Overview({ t }: any) {
  return (
    <section className="grid items-start gap-6 py-6 lg:grid-cols-[1.05fr_.95fr] lg:py-8">
      <div>
        <p className="mb-3 text-xs font-black uppercase tracking-[0.28em] text-pink">{t.heroKicker}</p>
        <h1 className="max-w-3xl font-display text-4xl font-black leading-[0.95] md:text-6xl">{t.heroTitle}</h1>
        <p className="mt-4 max-w-2xl text-base leading-7 text-muted md:text-lg">{t.heroBody}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button onClick={() => setHash("hatchery")}>{t.heroPrimary}</Button>
          <Button variant="secondary" onClick={() => setHash("growth")}>
            {t.heroSecondary}
          </Button>
        </div>
        <div className="mt-6 rounded-full border border-black/10 bg-white/80 px-5 py-3 text-sm font-bold text-muted shadow-sm">
          <span className="text-gold">{t.socialProof.split(" · ")[0]}</span> · {t.socialProof.split(" · ").slice(1).join(" · ")}
        </div>
      </div>
      <Card className="relative overflow-hidden p-6">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.28em] text-gold">{t.demoTitle}</p>
            <h2 className="mt-2 text-3xl font-black">{t.demoName}</h2>
          </div>
          <div className="grid h-20 w-20 animate-float place-items-center rounded-[1.5rem] border-2 border-pink bg-white p-[2px]">
            <div className="grid h-full w-full place-items-center rounded-[1.85rem] bg-sovereign">
              <Baby className="text-pink" size={36} />
            </div>
          </div>
        </div>
        <p className="mb-5 font-bold text-mint">{t.demoMood}</p>
        {[t.demoLine1, t.demoLine2, t.demoLine3].map((line: string) => (
          <div key={line} className="mb-3 flex gap-3 rounded-2xl border border-black/10 bg-white/80 p-4">
            <CheckCircle2 className="mt-0.5 text-mint" size={18} />
            <span className="text-sm leading-6 text-muted">{line}</span>
          </div>
        ))}
      </Card>
    </section>
  );
}

function CompetitiveUpgrade({ t }: any) {
  const rivals = [
    ["Replika / Nomi / Kindroid", "Strong memory, character identity, and daily companionship.", "Risk of emotional over-dependence, unclear action boundaries, and weak ecosystem utility."],
    ["Character.AI-style bots", "Instant roleplay, broad character choice, and low-friction conversation.", "Often entertainment-first, not tied to verified routines, permissions, or real daily tasks."],
    ["Wellness habit apps", "Good streaks, reminders, and habit formation.", "Usually no living companion identity and no cross-Sovasta coordination layer."],
  ];
  const upgrades = [
    ["Permissioned memory", "Memory is shown as evidence cards. The user can approve, pause, or delete it instead of trusting a hidden black box."],
    ["Useful companion loop", "Companion actions connect to Sovasta Audio, Sovasta Music, calendar-style routines, and future Sovasta surfaces, so it is not just chat."],
    ["Dependency guardrails", "The product copy and UX frame Mira as a helper with boundaries, not a replacement for human relationships or medical care."],
    ["Retention without manipulation", "Daily check-ins, skill unlocks, and weekly evidence reports create loyalty while keeping human control explicit."],
  ];
  return (
    <section className="mt-8">
      <SectionIntro
        kicker="COMPETITIVE UPGRADE"
        title="Built like a companion, governed like a trust product."
        body="The upgraded direction keeps the egg, growth, and skill-tree idea, but borrows the best parts of companion apps while fixing their weakest points: hidden memory, emotional overreach, and lack of real-world utility."
      />
      <div className="grid gap-5 lg:grid-cols-3">
        {rivals.map(([name, strength, gap]) => (
          <Card key={name}>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-pink">Market lesson</p>
            <h3 className="mt-3 text-xl font-black">{name}</h3>
            <p className="mt-4 text-sm leading-6 text-muted"><b className="text-text">Keep:</b> {strength}</p>
            <p className="mt-3 text-sm leading-6 text-muted"><b className="text-text">Fix:</b> {gap}</p>
          </Card>
        ))}
      </div>
      <div className="mt-5 grid gap-5 md:grid-cols-2">
        {upgrades.map(([title, body]) => (
          <Card key={title}>
            <div className="mb-4 inline-flex rounded-full border border-pink/30 bg-pink/10 px-3 py-1 text-xs font-black text-pink">Sovasta advantage</div>
            <h3 className="text-2xl font-black">{title}</h3>
            <p className="mt-3 leading-7 text-muted">{body}</p>
          </Card>
        ))}
      </div>
      <Card className="mt-5">
        <h3 className="text-2xl font-black">Conversion path</h3>
        <p className="mt-3 leading-7 text-muted">
          Free users hatch one local companion. Paid conversion is triggered when they want synced memory, cross-site skill actions,
          family/team rules, or weekly evidence reports. This keeps the core idea intact while making the business value visible.
        </p>
      </Card>
    </section>
  );
}

function Onboarding({ t, onClose }: any) {
  const scenes = [
    [t.scene1, t.scene1Body],
    [t.scene2, t.scene2Body],
    [t.scene3, t.scene3Body],
    [t.scene4, t.scene4Body],
  ];
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-sovereign/80 p-5 backdrop-blur-md">
      <Card className="max-w-4xl border-pink/30 bg-white/95 text-text">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-3xl font-black">{t.onboardingTitle}</h2>
            <p className="mt-2 text-muted">{t.onboardingSubtitle}</p>
          </div>
          <Button variant="secondary" onClick={onClose}>
            {t.close}
          </Button>
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {scenes.map(([title, body], index) => (
            <div key={title} className="rounded-3xl border border-black/10 bg-white/85 p-5">
              <div className="mb-4 h-1.5 rounded-full bg-black/10">
                <div className="h-full rounded-full bg-pink" style={{ width: `${(index + 1) * 25}%` }} />
              </div>
              <h3 className="font-black">{title}</h3>
              <p className="mt-3 text-sm leading-6 text-muted">{body}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function Hatchery({ t, draft, setDraft }: any) {
  return (
    <section>
      <SectionIntro kicker="LOCAL FIRST" title={t.hatchTitle} body={t.hatchBody} />
      <div className="grid gap-5 lg:grid-cols-[.95fr_1.05fr]">
        <Card>
          {[
            ["name", t.companionName],
            ["purpose", t.purpose],
            ["boundary", t.boundary],
          ].map(([key, label]) => (
            <label key={key} className="mb-5 block">
              <span className="mb-2 block text-sm font-black text-muted">{label}</span>
              <textarea
                value={draft[key]}
                onChange={(event) => setDraft({ ...draft, [key]: event.target.value })}
                rows={key === "name" ? 1 : 3}
                className="w-full resize-none rounded-2xl border border-black/10 bg-white px-4 py-3 text-text outline-none focus:border-pink"
              />
            </label>
          ))}
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => markGuideUsedGlobal("hatchery")}>{t.generate}</Button>
            <Button variant="secondary" onClick={() => (window.location.href = "/api/sove/login")}>
              {t.save}
            </Button>
          </div>
        </Card>
        <Card>
          <p className="text-xs font-black uppercase tracking-[0.3em] text-pink">{t.draftCard}</p>
          <h2 className="mt-4 text-4xl font-black">{draft.name || "Mira"}</h2>
          <p className="mt-4 leading-7 text-muted">{draft.purpose}</p>
          <div className="mt-6 rounded-3xl border border-gold/30 bg-gold/10 p-5 text-sm leading-6 text-gold">{draft.boundary}</div>
          <div className="mt-6 grid grid-cols-5 gap-2">
            {["INT", "EMP", "MEM", "AUT", "TRUST"].map((stat, i) => (
              <div key={stat} className="rounded-2xl bg-black/5 p-3 text-center">
                <div className="text-lg font-black text-mint">{62 + i * 6}</div>
                <div className="text-[10px] font-black text-muted">{stat}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </section>
  );
}

function Growth({ t }: any) {
  const stats = [
    [Brain, t.statInt, 74],
    [HeartHandshake, t.statEmp, 81],
    [Star, t.statMem, 48],
    [Zap, t.statAut, 32],
    [ShieldCheck, t.statTrust, 86],
  ];
  return (
    <section>
      <SectionIntro kicker="COMPANION LOOP" title={t.growthTitle} body={t.growthBody} />
      <div className="grid gap-5 md:grid-cols-3">
        <Metric label={t.level} value="7" />
        <Metric label={t.streak} value="3" />
        <Metric label={t.nextUnlock} value="Care+" />
      </div>
      <div className="mt-5 grid gap-5 md:grid-cols-5">
        {stats.map(([Icon, label, value]: any) => (
          <Card key={label}>
            <Icon className="mb-5 text-pink" />
            <div className="text-3xl font-black">{value}</div>
            <div className="mt-2 text-sm font-bold text-muted">{label}</div>
            <div className="mt-4 h-2 rounded-full bg-black/10">
              <div className="h-full rounded-full bg-pink" style={{ width: `${value}%` }} />
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
}

function Skills({ t }: any) {
  const skills = [
    [HeartHandshake, t.skillCare, t.skillCareBody],
    [Brain, t.skillWork, t.skillWorkBody],
    [MessageCircle, t.skillSocial, t.skillSocialBody],
    [LockKeyhole, t.skillTrust, t.skillTrustBody],
  ];
  return (
    <section>
      <SectionIntro kicker="PERMISSION TREE" title={t.skillsTitle} body={t.skillsBody} />
      <div className="grid gap-5 md:grid-cols-2">
        {skills.map(([Icon, title, body]: any) => (
          <Card key={title} className="group">
            <Icon className="mb-5 text-gold transition group-hover:scale-110" />
            <h3 className="text-2xl font-black">{title}</h3>
            <p className="mt-3 leading-7 text-muted">{body}</p>
            <Button variant="secondary" className="mt-6">
              Unlock <ChevronRight size={16} />
            </Button>
          </Card>
        ))}
      </div>
    </section>
  );
}

function Pricing({ t }: any) {
  const plans = [
    ["Free", "$0", "Included in Sovasta — one subscription across every Sovasta solution."],
    ["Basic", "$9", "Included in Sovasta — one subscription across every Sovasta solution."],
    ["Pro", "$29", "Included in Sovasta — one subscription across every Sovasta solution."],
    ["Premium", "$99", "Included in Sovasta — one subscription across every Sovasta solution."],
  ];
  return (
    <section>
      <SectionIntro kicker="UNIFIED BILLING" title={t.pricingTitle} body="Companion is included in Sovasta, a single subscription covering every Sovasta solution — no more per-solution checkout." />
      <div className="grid gap-5 lg:grid-cols-4">
        {plans.map(([name, price, feature], index) => (
          <Card key={name} className={index === 1 ? "border-pink/60 shadow-md" : ""}>
            <h3 className="text-2xl font-black">{name}</h3>
            <div className="my-5 text-5xl font-black">{price}<span className="text-base text-muted">/mo</span></div>
            <p className="min-h-16 text-sm leading-6 text-muted">{feature}</p>
            <Button className="mt-6" variant={index === 1 ? "primary" : "secondary"} onClick={() => (window.location.href = "https://hub.sovetrust.com/#bundle")}>
              {t.choose}
            </Button>
          </Card>
        ))}
      </div>
    </section>
  );
}

function Support({ t }: any) {
  return (
    <section>
      <SectionIntro kicker="SUPPORT" title={t.supportTitle} body={t.supportBody} />
      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <h3 className="mb-4 text-2xl font-black">{t.askGuide}</h3>
          <textarea className="h-36 w-full rounded-2xl border border-black/10 bg-white p-4 outline-none focus:border-pink" placeholder={t.message} />
          <Button className="mt-4">{t.send}</Button>
        </Card>
        <Card>
          <h3 className="mb-4 text-2xl font-black">{t.contactPerson}</h3>
          <input className="mb-3 w-full rounded-2xl border border-black/10 bg-white p-4 outline-none focus:border-pink" placeholder={t.subject} />
          <textarea className="h-24 w-full rounded-2xl border border-black/10 bg-white p-4 outline-none focus:border-pink" placeholder={t.message} />
          <Button variant="secondary" className="mt-4" onClick={() => (window.location.href = "/api/sove/login")}>
            {t.signin}
          </Button>
        </Card>
      </div>
      <Card className="mt-5">
        <h3 className="text-2xl font-black">{t.faqTitle}</h3>
        {[
          [t.faq1, t.faq1Body],
          [t.faq2, t.faq2Body],
          [t.faq3, t.faq3Body],
        ].map(([q, a]) => (
          <details key={q} className="border-b border-black/10 py-5">
            <summary className="cursor-pointer font-black">{q}</summary>
            <p className="mt-3 text-muted">{a}</p>
          </details>
        ))}
      </Card>
    </section>
  );
}

const COMMUNITY_CATS: Record<string, string> = { general: "catGeneral", skills: "catSkills", growth: "catGrowth", billing: "catBilling" };

function Community({ t, user, onOpenAuth }: any) {
  const [view, setView] = useState<"list" | "post">("list");
  const [posts, setPosts] = useState<any[] | null>(null);
  const [active, setActive] = useState<any>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("general");
  const [note, setNote] = useState("");
  const [consentOpen, setConsentOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const loadPosts = () => {
    fetch("/api/community/posts", { credentials: "same-origin" })
      .then((r) => r.json())
      .then((data) => setPosts(data?.ok ? data.posts : []))
      .catch(() => setPosts([]));
  };
  useEffect(() => { if (user && view === "list") loadPosts(); }, [user, view]);

  const submitPost = async () => {
    if (body.trim().length < 10) { setNote(t.communityNeedMore); return; }
    const res = await fetch("/api/community/posts", {
      method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" },
      body: JSON.stringify({ title: title.trim() || body.slice(0, 60), body: body.trim(), category }),
    });
    const data = await res.json().catch(() => null);
    if (res.status === 412 || data?.code === "CONSENT_REQUIRED") { setPendingAction(() => submitPost); setConsentOpen(true); return; }
    if (!data?.ok) { setNote(t.communityPostError); return; }
    setTitle(""); setBody(""); setNote(t.communityPostSent);
    markGuideUsedGlobal("community");
    loadPosts();
  };

  const openPost = async (id: string) => {
    const res = await fetch("/api/community/posts/" + encodeURIComponent(id), { credentials: "same-origin" });
    const data = await res.json().catch(() => null);
    if (!data?.ok) return;
    setActive(data); setView("post");
  };

  const vote = async (id: string) => {
    await fetch("/api/community/vote", { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify({ postId: id }) });
    loadPosts();
  };

  const agreeConsent = async () => {
    const res = await fetch("/api/consent", { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify({ kind: "community" }) });
    const data = await res.json().catch(() => null);
    setConsentOpen(false);
    if (data?.ok && pendingAction) pendingAction();
  };

  if (view === "post" && active) {
    return (
      <section>
        <Button variant="secondary" onClick={() => { setView("list"); setActive(null); }}>← {t.communityBack}</Button>
        <Card className="mt-4">
          <span className="rounded-full bg-black/5 px-3 py-1 text-xs font-black">{t[COMMUNITY_CATS[active.post.category]] || active.post.category}</span>
          <h2 className="mt-3 text-2xl font-black">{active.post.title}</h2>
          <p className="mt-1 text-xs text-muted">{active.post.author_email || "Member"} · {new Date(active.post.created_at).toLocaleDateString()}</p>
          <p className="mt-4 whitespace-pre-wrap leading-7 text-muted">{active.post.body}</p>
        </Card>
        <Card className="mt-4">
          <h3 className="mb-3 text-xl font-black">{t.communityReplies}</h3>
          {active.comments.length ? active.comments.map((c: any) => (
            <div key={c.id} className="border-b border-black/10 py-3">
              <p className="whitespace-pre-wrap text-sm text-muted">{c.body}</p>
              <p className="mt-1 text-xs text-muted">{c.author_email || "Member"} · {new Date(c.created_at).toLocaleDateString()}</p>
            </div>
          )) : <p className="text-muted">{t.communityNoReplies}</p>}
          <label className="mt-4 block">
            <span className="mb-2 block text-sm font-black text-muted">{t.communityYourReply}</span>
            <textarea id="cc-reply" rows={3} className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 outline-none focus:border-pink" />
          </label>
          <Button className="mt-3" onClick={async () => {
            const el = document.getElementById("cc-reply") as HTMLTextAreaElement;
            const text = el.value.trim();
            if (text.length < 2) return;
            const res = await fetch("/api/community/comments", { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify({ postId: active.post.id, body: text }) });
            const data = await res.json().catch(() => null);
            if (res.status === 412 || data?.code === "CONSENT_REQUIRED") { setPendingAction(() => () => openPost(active.post.id)); setConsentOpen(true); return; }
            if (data?.ok) openPost(active.post.id);
          }}>{t.communityPostReply}</Button>
        </Card>
        {consentOpen && <ConsentModal t={t} onAgree={agreeConsent} onClose={() => setConsentOpen(false)} />}
      </section>
    );
  }

  return (
    <section>
      <SectionIntro kicker={t.communityEyebrow} title={t.communityTitle} body={t.communityBody} />
      <div className="grid gap-5 lg:grid-cols-2">
        <div>
          {!user ? (
            <Card>
              <h3 className="mb-3 text-xl font-black">{t.communityWallTitle}</h3>
              <p className="mb-4 text-muted">{t.communityWallGate}</p>
              <Button onClick={onOpenAuth}>{t.signin}</Button>
            </Card>
          ) : (
            <>
              <Card>
                <label className="mb-4 block">
                  <span className="mb-2 block text-sm font-black text-muted">{t.communityShareLabel}</span>
                  <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 outline-none focus:border-pink" />
                </label>
                <label className="mb-4 block">
                  <span className="mb-2 block text-sm font-black text-muted">{t.communityTitleLabel}</span>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 outline-none focus:border-pink" />
                </label>
                <label className="mb-4 block">
                  <span className="mb-2 block text-sm font-black text-muted">{t.communityTopicLabel}</span>
                  <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 outline-none focus:border-pink">
                    {Object.entries(COMMUNITY_CATS).map(([k, labelKey]) => <option key={k} value={k}>{t[labelKey]}</option>)}
                  </select>
                </label>
                <Button onClick={submitPost}>{t.communityPost}</Button>
                {note && <p className="mt-3 text-sm text-muted">{note}</p>}
              </Card>
              <Card className="mt-4">
                {posts === null ? <p className="text-muted">Loading…</p> : posts.length === 0 ? <p className="text-muted">{t.communityEmpty}</p> : posts.map((p) => (
                  <div key={p.id} className="flex items-start justify-between gap-3 border-b border-black/10 py-3">
                    <div className="cursor-pointer" onClick={() => openPost(p.id)}>
                      <span className="rounded-full bg-black/5 px-3 py-1 text-xs font-black">{t[COMMUNITY_CATS[p.category]] || p.category}</span>
                      <p className="mt-2 font-black">{p.title}</p>
                      <p className="mt-1 text-sm text-muted">{p.body.slice(0, 140)}{p.body.length > 140 ? "…" : ""}</p>
                      <p className="mt-1 text-xs text-muted">{p.author_email || "Member"} · {p.comment_count} {t.communityReplies}</p>
                    </div>
                    <button onClick={() => vote(p.id)} className={`rounded-full border px-3 py-1 text-sm font-black ${p.voted ? "border-pink bg-pink/10 text-pink" : "border-black/10"}`}>▲ {p.votes}</button>
                  </div>
                ))}
              </Card>
            </>
          )}
        </div>
        <CommunityExternal t={t} />
      </div>
      {consentOpen && <ConsentModal t={t} onAgree={agreeConsent} onClose={() => setConsentOpen(false)} />}
    </section>
  );
}

function CommunityExternal({ t }: any) {
  const topics: [string, string][] = [["skills", "Sovasta Companion permission skills"], ["growth", "Sovasta Companion growth loop"]];
  return (
    <Card>
      <h3 className="mb-2 text-xl font-black">{t.externalTitle}</h3>
      <p className="mb-4 text-sm text-muted">{t.externalText}</p>
      {topics.map(([cat, q]) => (
        <div key={cat} className="flex items-center justify-between border-b border-black/10 py-3">
          <b className="text-sm">{t[COMMUNITY_CATS[cat]]}</b>
          <span className="flex gap-2">
            <a className="rounded-full border border-black/10 px-3 py-1 text-xs font-black" target="_blank" rel="noopener noreferrer" href={`https://www.reddit.com/search/?q=${encodeURIComponent(q)}`}>Reddit ↗</a>
            <a className="rounded-full border border-black/10 px-3 py-1 text-xs font-black" target="_blank" rel="noopener noreferrer" href={`https://www.quora.com/search?q=${encodeURIComponent(q)}`}>Quora ↗</a>
          </span>
        </div>
      ))}
      <div className="mt-4 flex flex-wrap gap-2">
        <a className="rounded-full border border-black/10 px-3 py-1 text-xs font-black" target="_blank" rel="noopener noreferrer" href={`https://www.reddit.com/submit?url=${encodeURIComponent("https://companion.sovetrust.com")}&title=${encodeURIComponent("Sovasta Companion — a permissioned AI companion")}`}>{t.startRedditPost}</a>
        <a className="rounded-full border border-black/10 px-3 py-1 text-xs font-black" target="_blank" rel="noopener noreferrer" href="https://www.reddit.com/r/artificial/">{t.joinSubreddit}</a>
        <a className="rounded-full border border-black/10 px-3 py-1 text-xs font-black" target="_blank" rel="noopener noreferrer" href="https://www.quora.com/search?q=AI%20companion%20apps">{t.exploreQuora}</a>
      </div>
      <p className="mt-4 text-xs text-muted">{t.externalDisclosure}</p>
    </Card>
  );
}

function ConsentModal({ t, onAgree, onClose }: any) {
  const [checked, setChecked] = useState(false);
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-sovereign/80 p-5 backdrop-blur-md" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Card className="max-w-md border-pink/30 bg-white/95 text-text">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-xl font-black">{t.consentTitle}</h2>
          <Button variant="secondary" onClick={onClose}>{t.close}</Button>
        </div>
        <p className="text-sm text-muted">{t.consentText}</p>
        <label className="mt-4 flex items-start gap-2 text-sm">
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} className="mt-1" />
          <span>{t.consentCheckbox}</span>
        </label>
        <Button className="mt-4" onClick={onAgree} disabled={!checked}>{t.consentAgree}</Button>
      </Card>
    </div>
  );
}

function Account({ t, user, onOpenAuth }: any) {
  return (
    <section>
      <SectionIntro kicker="ONE SOVE ID" title={t.accountTitle} body={t.accountBody} />
      <Card>
        {user ? (
          <p className="mb-4 text-lg font-black">{user.email}</p>
        ) : (
          <p className="mb-4 text-muted">{t.communityWallGate}</p>
        )}
        <div className="flex flex-wrap gap-3">
          {!user && <Button onClick={onOpenAuth}>{t.signin}</Button>}
          <Button variant="secondary" onClick={() => (window.location.href = "/legal/terms")}>
            {t.terms}
          </Button>
          <Button variant="secondary" onClick={() => (window.location.href = "/legal/privacy")}>
            {t.privacy}
          </Button>
        </div>
      </Card>
    </section>
  );
}

function SectionIntro({ kicker, title, body }: { kicker: string; title: string; body: string }) {
  return (
    <div className="mb-5 max-w-3xl">
      <p className="mb-3 text-xs font-black uppercase tracking-[0.28em] text-pink">{kicker}</p>
      <h1 className="font-display text-4xl font-black leading-tight md:text-6xl">{title}</h1>
      <p className="mt-4 max-w-3xl text-base leading-7 text-muted md:text-lg">{body}</p>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <div className="text-4xl font-black text-gold">{value}</div>
      <div className="mt-2 text-sm font-bold text-muted">{label}</div>
    </Card>
  );
}

function Footer({ t }: any) {
  return (
    <footer className="relative z-10 border-t border-black/10 bg-white px-5 py-10 text-text shadow-[0_-10px_30px_rgba(16,32,26,0.04)]">
      <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div className="max-w-sm">
          <button onClick={() => setHash("overview")} className="flex items-center gap-3 font-black">
            <span className="grid h-9 w-9 place-items-center rounded-2xl border border-pink/50 bg-white text-pink">
              <Sparkles size={18} />
            </span>
            {t.product}
          </button>
          <p className="mt-4 text-sm leading-6 text-muted">{t.footer}</p>
          <p className="mt-5 text-xs font-bold text-muted">© 2026 Sovasta Trust. All rights reserved.</p>
        </div>
        <div>
          <h3 className="mb-4 text-xs font-black uppercase tracking-[0.22em] text-pink">Product</h3>
          <div className="flex flex-col gap-3 text-sm font-bold text-muted">
            {nav.slice(0, 4).map((item) => (
              <button key={item} onClick={() => setHash(item)} className="text-left hover:text-text">
                {t[item]}
              </button>
            ))}
          </div>
        </div>
        <div>
          <h3 className="mb-4 text-xs font-black uppercase tracking-[0.22em] text-pink">Account</h3>
          <div className="flex flex-col gap-3 text-sm font-bold text-muted">
            <button onClick={() => setHash("pricing")} className="text-left hover:text-text">{t.pricing}</button>
            <button onClick={() => setHash("support")} className="text-left hover:text-text">{t.support}</button>
            <button onClick={() => setHash("account")} className="text-left hover:text-text">{t.account}</button>
            <button onClick={() => realInstall()} className="text-left hover:text-text">{t.install}</button>
          </div>
        </div>
        <div>
          <h3 className="mb-4 text-xs font-black uppercase tracking-[0.22em] text-pink">Legal</h3>
          <div className="flex flex-col gap-3 text-sm font-bold text-muted">
            <a href="/legal/terms" className="hover:text-text">{t.terms}</a>
            <a href="/legal/privacy" className="hover:text-text">{t.privacy}</a>
            <a href="https://hub.sovetrust.com" className="hover:text-text">Sovasta Hub</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

